// File: src/pages/neutral/NeutralWhereToMeetPage.jsx
import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LuArrowLeft, LuMapPin, LuNavigation } from 'react-icons/lu';
import EventHelpTooltip from '../../components/EventPreview/EventHelpTooltip';
import { ReactComponent as TribeLogo } from '../../assets/TribeLogo.svg';

const COLOR = {
  bg: '#FFFFFF',
  ink: '#0B1020',
  sub: '#3A4050',
  line: '#E7E9EE',
  chip: '#eeee',
};

const GRIDW = 'min(720px, 92vw)';

export default function NeutralWhereToMeetPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { eventId } = useParams();

  const event = state?.event || {};

  const spot =
    event?.whereToMeetSpot ||
    'Near the front entrance by the patio.';

  const note =
    event?.whereToMeetNote ||
    'Look for your host or check the group chat for updates when they arrive.';

  const imageUrl = event?.whereToMeetImageUrl || '';

  const handleBack = () => {
    navigate(`/neutral/${eventId}/intro`, { state: { event } });
  };

  const handleContinue = () => {
    navigate(`/neutral/${eventId}/portal`, { state: { event } });
  };

  const handleDirections = () => {
    if (!event?.latitude || !event?.longitude) {
      alert('No location available for this gathering.');
      return;
    }

    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${event.latitude},${event.longitude}`,
      '_blank'
    );
  };

  return (
    <div style={S.app}>
      <header style={S.header}>
        <button
          style={S.backBtn}
          onClick={handleBack}
          aria-label="Go back"
          type="button"
        >
          <LuArrowLeft size={16} />
        </button>

        <div style={S.logoWrap} aria-hidden="true">
          <TribeLogo style={S.logoSvg} />
        </div>

        <div style={S.rightWrap}>
          <EventHelpTooltip
            title="Where to meet"
            body="Use this page to find the group when you arrive. Then enter the chat portal to coordinate if you need help."
          />
        </div>
      </header>

      <main style={S.page}>
        <section style={S.headline}>
          <div style={S.iconCircle}>
            <LuMapPin size={20} strokeWidth={2.35} />
          </div>

          <h1 style={S.title}>Where to meet</h1>

          <p style={S.subtitle}>
            Go here first when you arrive. Then enter the chat portal if you need help finding the group.
          </p>
        </section>

        <section style={S.meetSection}>
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="Meeting spot"
              style={S.meetImage}
              draggable={false}
            />
          ) : (
            <div style={S.meetImageFallback}>
              <LuMapPin size={34} strokeWidth={2.2} />
            </div>
          )}

          <div style={S.copy}>
            <div style={S.spot}>{spot}</div>

            <p style={S.note}>{note}</p>
          </div>
        </section>
      </main>

      <div style={S.buttonBar}>
        <div style={S.buttonRow}>
          <button
            type="button"
            style={S.secondaryBtn}
            onClick={handleDirections}
          >
            <LuNavigation size={16} strokeWidth={2.3} />
            Get directions
          </button>

          <button
            type="button"
            style={S.primaryBtn}
            onClick={handleContinue}
          >
            Enter group chat
          </button>
        </div>
      </div>
    </div>
  );
}

const S = {
  app: {
    fontFamily:
      'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    color: COLOR.ink,
    background: COLOR.bg,
  },

  header: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    width: '100%',
    maxWidth: 1200,
    margin: '0 auto',
    height: 56,
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: '1fr auto 1fr',
    background: '#fff',
  },

  backBtn: {
    justifySelf: 'start',
    marginLeft: 12,
    background: '#000',
    color: '#fff',
    border: 'none',
    borderRadius: 9999,
    width: 40,
    height: 26,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },

  logoWrap: {
    justifySelf: 'center',
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
  },

  logoSvg: {
    width: 72,
    height: 'auto',
    display: 'block',
  },

  rightWrap: {
    justifySelf: 'end',
    marginRight: 12,
    display: 'flex',
    alignItems: 'center',
    height: 32,
  },

  page: {
    width: GRIDW,
    margin: '12px auto 104px',
    display: 'grid',
    gap: 18,
  },

  headline: {
    textAlign: 'center',
    maxWidth: 560,
    margin: '0 auto',
  },

  iconCircle: {
    width: 42,
    height: 42,
    borderRadius: 999,
    background: COLOR.chip,
    color: '#111',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 12px',
  },

  title: {
    margin: 0,
    color: '#0B1020',
    fontSize: 'clamp(2rem, 7vw, 2.55rem)',
    lineHeight: 0.98,
    fontWeight: 900,
    letterSpacing: '-0.055em',
  },

  subtitle: {
    maxWidth: 320,
    margin: '12px auto 0',
    color: COLOR.sub,
    fontSize: 13.5,
    lineHeight: 1.45,
    fontWeight: 500,
    letterSpacing: '-0.016em',
  },

  meetSection: {
    width: '100%',
  },

  meetImage: {
    width: '100%',
    height: 330,
    objectFit: 'cover',
    display: 'block',
    borderRadius: 16,
    background: '#F4F4F5',
  },

  meetImageFallback: {
    width: '100%',
    height: 300,
    borderRadius: 16,
    background: '#F4F4F5',
    color: '#111',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  copy: {
    padding: '16px 2px 0',
  },

  spot: {
    color: '#111',
    fontSize: 18,
    lineHeight: 1.16,
    fontWeight: 850,
    letterSpacing: '-0.04em',
  },

  note: {
    margin: '9px 0 0',
    color: 'rgba(17,17,17,0.68)',
    fontSize: 13.5,
    lineHeight: 1.42,
    fontWeight: 500,
    letterSpacing: '-0.016em',
  },

  buttonBar: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    padding: '12px 20px calc(12px + env(safe-area-inset-bottom))',
    background: '#fff',
    borderTop: `1px solid ${COLOR.line}`,
    zIndex: 20,
  },

  buttonRow: {
    margin: '0 auto',
    width: '100%',
    maxWidth: 720,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
  },

  secondaryBtn: {
    height: 44,
    borderRadius: 10,
    border: '1px solid #DADDE1',
    background: '#fff',
    color: '#111',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    cursor: 'pointer',
  },

  primaryBtn: {
    height: 44,
    borderRadius: 10,
    border: '1px solid #0b0b0b',
    background: '#0b0b0b',
    color: '#fff',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
};